import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { SiteFooter } from '../components/Layout';
import { Loading } from '../components/ui';
import { api } from '../lib/api';
import { date } from '../lib/format';

interface PublishedNotice {
  id: string;
  version: string;
  title: string;
  summary: string;
  content: string;
  publishedAt: string | null;
  effectiveAt: string | null;
}

/**
 * SARTHI's privacy policy, as published from the platform console.
 *
 * The text is not in this file: it is a versioned notice (Platform > Privacy
 * > Notices), drafted from a template whose business and legal placeholders
 * have to be replaced before it can be published. Until somebody has done
 * that, this page says so plainly rather than showing placeholder text as if
 * it were a policy.
 */
export function PrivacyPolicy() {
  const policy = useQuery({
    queryKey: ['public-notice', 'PRIVACY_POLICY'],
    queryFn: () => api.get<PublishedNotice | null>('/api/public/notices/PRIVACY_POLICY'),
  });

  return (
    <div className="legal-shell">
      <header className="legal-head">
        <div className="brand-mark">S</div>
        <div>
          <h1>{policy.data?.title ?? 'SARTHI — Privacy Policy'}</h1>
          {policy.data ? (
            <div className="small">
              Version {policy.data.version}
              {policy.data.effectiveAt ? ` · effective ${date(policy.data.effectiveAt)}` : ''}
            </div>
          ) : null}
        </div>
        <Link className="btn sm" to="/">
          Back
        </Link>
      </header>

      <main className="legal-body">
        {policy.isLoading ? (
          <Loading />
        ) : policy.data ? (
          <>
            <p className="legal-lead">{policy.data.summary}</p>
            <div className="privacy-doc">{policy.data.content}</div>
          </>
        ) : (
          <p className="legal-lead">
            The privacy policy has not been published yet. Earlier versions, once published, are
            kept and can be requested from the contact listed in the Terms and Conditions.
          </p>
        )}
        <p className="small muted">
          See also the <Link to="/terms">Terms and Conditions</Link>.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
